import {
  ArtifactType,
  RecurrenceType,
  Scalars,
  Shipment,
  ShipmentStatus,
  ShipmentType,
  Stop,
  StopCompletionReason,
  TaskStatus,
  UserRoleType,
} from "@api/graphql/generated/generated-types";
import { DriverMapItem } from "@src/common/components/Widgets/DriverMapWidget";
import { BadgeMeta } from "../utils/stopBadgeFormatters";
import { ComponentProps, FC, ReactNode } from "react";

export enum DispatchCandidateSortingType {
  CLOSEST = "Closest",
  LEAST_DEVIATION = "Least Deviation",
  LEAST_WORKLOAD = "Least Workload",
  LEAST_TASKS = "Least Tasks",
  ALPHABETICAL = "Alphabetical",
}
export interface StopColumn {
  badgeMeta: BadgeMeta;
  timeWindowText: string | undefined;
  eta: string | undefined;
  isLikelyLate: boolean | undefined;
  taskStatusList: TaskStatus[];
  arrivedAt: string | undefined;
  completedAt: string | undefined;
  completionReason: StopCompletionReason | undefined;
  instruction: string | undefined;
  dropOffStops?: Stop[];
  pickUpStops?: Stop[];
}

export interface ArtifactDisplay {
  id: string;
  url: string;
  caption: string | undefined;
  type: ArtifactType;
  timestamp: Date;
}

export interface ArtifactsAtStop {
  stopType: string;
  stopId: string;
  stopAddress: string;
  artifacts: ArtifactDisplay[];
}

export type ShipmentRow =
  | {
      key: string;
      id: string;
      status: string;
      shipmentStatus: ShipmentStatus;
      StatusIcon: FC<ComponentProps<"svg">>;
      orderDisplayId: string | number;
      customerDisplayName: string;
      pickup: StopColumn;
      dropoff: StopColumn;
      origin: {
        dispatchZone: string;
        streetAddress: string;
        dispatchZoneLine2: string | undefined;
        /** A pickup on this order sits at a 0,0 placeholder: its address must be fixed before dispatch. */
        isLocationNotFound: boolean;
      };
      destination: {
        dispatchZone: string;
        streetAddress: string;
        dispatchZoneLine2: string | undefined;
        /** A drop-off on this order sits at a 0,0 placeholder: its address must be fixed before dispatch. */
        isLocationNotFound: boolean;
      };
      package: {
        numPackages: number | undefined;
        packageSize: string;
        packageWeightInPounds: number | undefined;
      };
      shipmentDate: Scalars["Date"];
      serviceType: string;
      serviceTypeColor: string | undefined;
      driverId: string | undefined;
      driverPhotoUrl: string | undefined;
      driverName: string | undefined;
      driverMapItem: DriverMapItem | undefined;
      driverAvatarDisplay: string | undefined;
      canUnassignDriver: boolean;
      canChangeRouting: boolean;
      canDeleteOrder: boolean;
      canFailOrder: boolean;
      canCloseOrder: boolean;
      canUncloseOrder: boolean;
      canForceCompleteOrder: boolean;
      canForceAttemptOrder: boolean;
      artifactsAtStops?: ArtifactsAtStop[];
      recurrenceType: RecurrenceType | undefined;
      isCompleted: boolean;
      shipmentType: ShipmentType;
      shipment: Shipment | undefined;
      isMultiDay: boolean;
    }
  | undefined;

export enum ShipmentsPrimaryFilterType {
  ACTIVE = "ACTIVE",
  ALL = "ALL",
  LATE = "LATE",
  LATE_IN_15_MINUTES = "LATE_IN_15_MINUTES",
  LATE_IN_30_MINUTES = "LATE_IN_30_MINUTES",
  LATE_IN_60_MINUTES = "LATE_IN_60_MINUTES",
  LIKELY_LATE = "LIKELY_LATE",
  DELIVERED = "DELIVERED",
  CLOSED = "CLOSED",
  UNASSIGNED = "UNASSIGNED",
  ASSIGNED = "ASSIGNED",
  ON_HOLD = "ON_HOLD",
  CUSTOM = "CUSTOM",
}

export type TableActionDropdownItem = {
  key: string;
  label: ReactNode;
  onClick: (row: ShipmentRow) => void;
  disabled: (row: ShipmentRow) => boolean;
  icon: React.ReactNode;
  tooltip: (row: ShipmentRow) => string | undefined;
  minimumUserRoleType: UserRoleType;
};

export interface ShipmentOverviewCounts {
  numLate: number | undefined;
  numLateIn15Minutes: number | undefined;
  numLateIn30Minutes: number | undefined;
  numLateIn60Minutes: number | undefined;
  numLikelyLate: number | undefined;
}

export interface CustomFilter {
  name: string;
  endCustomerIds?: string[] | null;
  serviceTypeIds?: string[] | null;
  shipmentStatuses?: ShipmentStatus[] | null;
  excludeWordsFromNote?: string[] | null;
  driverIds?: string[] | null;
  isConfirmed?: boolean | null;
  isAttempted?: boolean | null;
  recurrenceTypes?: RecurrenceType[] | null;
}

export interface CustomFilterOverview {
  name: string;
  count: number;
}
